/**
 * Date/time helpers that always work in the user's LOCAL timezone.
 *
 * The built-in `new Date().toISOString()` returns a UTC string (Z-suffix),
 * which causes <input type="date"> and <input type="datetime-local"> to show
 * the wrong date/time for users in negative UTC-offset timezones (e.g. US CST
 * is UTC-6, so after 6 PM UTC the UTC date is already "tomorrow").
 *
 * These helpers produce the same `YYYY-MM-DD` / `YYYY-MM-DDTHH:MM` formats
 * that HTML date inputs expect, but using local wall-clock time.
 */

/** Returns the current local date as "YYYY-MM-DD" */
export function localDateString(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Returns the local date portion of a timestamp in the browser timezone. */
export function toLocalDateString(value = new Date()) {
  const date = parseDisplayTimestamp(value)
  if (!date) return localDateString(new Date())
  return localDateString(date)
}

/** Returns the current local datetime as "YYYY-MM-DDTHH:MM" */
export function localDateTimeString(date = new Date()) {
  const h = String(date.getHours()).padStart(2, '0')
  const mi = String(date.getMinutes()).padStart(2, '0')
  return `${localDateString(date)}T${h}:${mi}`
}

/** Convert a Date instance to a UTC ISO string for API persistence. */
export function toUtcIsoString(date = new Date()) {
  return date.toISOString()
}

/** Parse a value that may be either a full ISO string or a local datetime string.
 *  This preserves the wall-clock time from the source instead of reinterpreting
 *  it as a different timezone.
 */
export function parseDisplayTimestamp(value) {
  if (!value) return null
  if (typeof value !== 'string') return new Date(value)

  const trimmed = value.trim()
  if (!trimmed) return null

  // Handle local datetime values like "2026-06-30T22:48" or "2026-06-30T22:48:00"
  const localParts = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/)
  if (localParts) {
    const [, y, mo, d, h, mi] = localParts
    return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi))
  }

  // Handle common SQL-like format: "YYYY-MM-DD HH:MM[:SS[.SSS]]"
  const spacedLocalParts = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/)
  if (spacedLocalParts) {
    const [, y, mo, d, h, mi] = spacedLocalParts
    return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi))
  }

  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return null
  return date
}

/** Format a timestamp for display in the user's local timezone. */
export function formatLocalizedTime(value, options = {}) {
  if (!value) return '—'
  const date = parseDisplayTimestamp(value)
  if (!date) return '—'
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options,
  })
}

/** Format a timestamp for display in the user's local timezone with date + time. */
export function formatLocalizedDateTime(value, options = {}) {
  if (!value) return '—'
  const date = parseDisplayTimestamp(value)
  if (!date) return '—'
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  })
}
